import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RefreshCw, PlusCircle, CheckCircle, Plane, ChevronLeft, ChevronRight, X, Video } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useMediaServerConnection } from "@/hooks/useMediaServerConnection";
import "./OpsDashboard.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL = 60 * 60 * 1000; // 60 minutes auto-refresh (manual refresh always available)

// Live stream URLs for each FTX designator (WebRTC via new NSAS Video Server)
const LIVE_STREAM_URLS = {
  'SPARROW 3': 'https://srv-ops.nsas.tech:8443/nsas.html?id=sparrow3',
  'SPARROW3': 'https://srv-ops.nsas.tech:8443/nsas.html?id=sparrow3',
  'OSPREY 4': 'https://srv-ops.nsas.tech:8443/nsas.html?id=osprey4',
  'OSPREY4': 'https://srv-ops.nsas.tech:8443/nsas.html?id=osprey4',
};

// Get FTX designator from flight data
const getFTXDesignator = (flight) => {
  const callsign = flight?.callsign || flight?.flight_number || '';
  if (callsign.toUpperCase().includes('SPARROW') || callsign.toUpperCase().includes('OO-MSA')) {
    return 'SPARROW 3';
  }
  if (callsign.toUpperCase().includes('OSPREY') || callsign.toUpperCase().includes('OO-MSD')) {
    return 'OSPREY 4';
  }
  // Check aircraft registration
  const reg = flight?.aircraft_reg || '';
  if (reg === 'OO-MSA') return 'SPARROW 3';
  if (reg === 'OO-MSD') return 'OSPREY 4';
  return null;
};

// NSAS Logo URL
const NSAS_LOGO = "https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png";

// Confetti Component
const Confetti = ({ active }) => {
  if (!active) return null;
  
  // Reduced confetti - wave 1 and wave 2
  const confettiPieces = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 6,
    duration: 3 + Math.random() * 3,
    color: ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#00d2d3'][Math.floor(Math.random() * 6)]
  }));

  // Sequential firework bursts - wave 1
  const fireworks1 = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    left: 10 + Math.random() * 80,
    top: 10 + Math.random() * 40,
    delay: 0.5 + i * 0.4,
    scale: 0.8 + Math.random() * 0.4,
    color: ['#ff0000', '#ffff00', '#00ffff', '#ff00ff', '#00ff00', '#ffffff'][Math.floor(Math.random() * 6)]
  }));

  // Wave 2 - after first wave
  const fireworks2 = Array.from({ length: 8 }, (_, i) => ({
    id: i + 10,
    left: 15 + Math.random() * 70,
    top: 15 + Math.random() * 45,
    delay: 4 + i * 0.4,
    scale: 0.7 + Math.random() * 0.5,
    color: ['#ff0000', '#ffff00', '#00ffff', '#ff00ff', '#00ff00', '#ffffff'][Math.floor(Math.random() * 6)]
  }));

  // Wave 3 - later
  const fireworks3 = Array.from({ length: 6 }, (_, i) => ({
    id: i + 20,
    left: 10 + Math.random() * 80,
    top: 10 + Math.random() * 50,
    delay: 7.5 + i * 0.4,
    scale: 0.8 + Math.random() * 0.4,
    color: ['#ff0000', '#ffff00', '#00ffff', '#ff00ff', '#00ff00', '#ffffff'][Math.floor(Math.random() * 6)]
  }));

  // Fewer flares - sequential across the duration
  const flares = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    startY: 20 + Math.random() * 40,
    delay: 1 + i * 1.8,
    duration: 1,
    direction: i % 2 === 0 ? 'right' : 'left',
    color: ['#ffffff', '#ffff00', '#00ffff'][Math.floor(Math.random() * 3)]
  }));

  // Space Shuttles - diagonal flight
  const shuttles = Array.from({ length: 4 }, (_, i) => ({
    id: i,
    delay: i * 2.8,
    duration: 5,
    direction: i % 2 === 0 ? 'up-right' : 'up-left',
    scale: 1.5 + Math.random() * 0.5
  }));

  const allFireworks = [...fireworks1, ...fireworks2, ...fireworks3];

  return (
    <div className="confetti-container">
      {/* Waving USA Flag */}
      <div className="usa-flag-container">
        <svg viewBox="0 0 190 100" className="usa-flag">
          {/* Red and white stripes */}
          <rect x="0" y="0" width="190" height="7.69" fill="#B22234"/>
          <rect x="0" y="7.69" width="190" height="7.69" fill="#FFFFFF"/>
          <rect x="0" y="15.38" width="190" height="7.69" fill="#B22234"/>
          <rect x="0" y="23.07" width="190" height="7.69" fill="#FFFFFF"/>
          <rect x="0" y="30.76" width="190" height="7.69" fill="#B22234"/>
          <rect x="0" y="38.45" width="190" height="7.69" fill="#FFFFFF"/>
          <rect x="0" y="46.14" width="190" height="7.69" fill="#B22234"/>
          <rect x="0" y="53.83" width="190" height="7.69" fill="#FFFFFF"/>
          <rect x="0" y="61.52" width="190" height="7.69" fill="#B22234"/>
          <rect x="0" y="69.21" width="190" height="7.69" fill="#FFFFFF"/>
          <rect x="0" y="76.9" width="190" height="7.69" fill="#B22234"/>
          <rect x="0" y="84.59" width="190" height="7.69" fill="#FFFFFF"/>
          <rect x="0" y="92.28" width="190" height="7.72" fill="#B22234"/>
          {/* Blue canton */}
          <rect x="0" y="0" width="76" height="53.83" fill="#3C3B6E"/>
          {/* Stars - simplified grid */}
          {Array.from({ length: 9 }, (_, row) => 
            Array.from({ length: row % 2 === 0 ? 6 : 5 }, (_, col) => (
              <circle 
                key={`star-${row}-${col}`}
                cx={row % 2 === 0 ? 6 + col * 12.5 : 12 + col * 12.5}
                cy={3 + row * 5.8}
                r="2"
                fill="#FFFFFF"
              />
            ))
          )}
        </svg>
        <div className="flag-pole"></div>
      </div>
      
      {confettiPieces.map(piece => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            backgroundColor: piece.color
          }}
        />
      ))}
      {allFireworks.map(fw => (
        <div
          key={`fw-${fw.id}`}
          className="firework"
          style={{
            left: `${fw.left}%`,
            top: `${fw.top}%`,
            animationDelay: `${fw.delay}s`,
            transform: `scale(${fw.scale})`,
            '--fw-color': fw.color
          }}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <div
              key={i}
              className="firework-particle"
              style={{
                '--angle': `${i * 30}deg`,
                backgroundColor: fw.color,
                boxShadow: `0 0 4px 2px ${fw.color}`
              }}
            />
          ))}
          <div className="firework-core" style={{ backgroundColor: fw.color }} />
        </div>
      ))}
      {flares.map(flare => (
        <div
          key={`flare-${flare.id}`}
          className={`flare flare-${flare.direction}`}
          style={{
            top: `${flare.startY}%`,
            animationDelay: `${flare.delay}s`,
            animationDuration: `${flare.duration}s`,
            backgroundColor: flare.color,
            boxShadow: `0 0 8px 4px ${flare.color}`
          }}
        />
      ))}
      {shuttles.map(shuttle => (
        <div
          key={`shuttle-${shuttle.id}`}
          className={`space-shuttle shuttle-${shuttle.direction}`}
          style={{
            animationDelay: `${shuttle.delay}s`,
            animationDuration: `${shuttle.duration}s`,
            transform: `scale(${shuttle.scale})`
          }}
        >
          <svg viewBox="0 0 120 60" className="shuttle-svg">
            {/* Main body */}
            <ellipse cx="60" cy="30" rx="45" ry="12" fill="#e8e8e8" />
            {/* Nose cone */}
            <path d="M105,30 L120,30 L105,25 Z" fill="#1a1a2e" />
            <path d="M105,30 L120,30 L105,35 Z" fill="#2d3748" />
            {/* Cockpit windows */}
            <rect x="95" y="26" width="8" height="8" rx="1" fill="#1a1a2e" />
            {/* Wings */}
            <path d="M40,30 L20,50 L50,42 L55,30 Z" fill="#d1d5db" stroke="#9ca3af" strokeWidth="0.5" />
            <path d="M40,30 L20,10 L50,18 L55,30 Z" fill="#d1d5db" stroke="#9ca3af" strokeWidth="0.5" />
            {/* Tail fin */}
            <path d="M15,30 L5,10 L25,25 Z" fill="#d1d5db" stroke="#9ca3af" strokeWidth="0.5" />
            {/* Engine pods */}
            <ellipse cx="12" cy="35" rx="8" ry="4" fill="#4a5568" />
            <ellipse cx="12" cy="25" rx="8" ry="4" fill="#4a5568" />
            <ellipse cx="12" cy="30" rx="10" ry="5" fill="#4a5568" />
            {/* Engine flames */}
            <ellipse cx="2" cy="30" rx="6" ry="8" fill="#ff6600" opacity="0.9" />
            <ellipse cx="0" cy="30" rx="4" ry="5" fill="#ffff00" opacity="0.9" />
            <ellipse cx="2" cy="35" rx="4" ry="3" fill="#ff6600" opacity="0.7" />
            <ellipse cx="2" cy="25" rx="4" ry="3" fill="#ff6600" opacity="0.7" />
            {/* USA text */}
            <text x="70" y="33" fontSize="6" fill="#1a1a2e" fontWeight="bold">USA</text>
          </svg>
          {/* Exhaust trail */}
          <div className="exhaust-trail"></div>
        </div>
      ))}
    </div>
  );
};

// Airport coordinates for Windy map
const AIRPORT_COORDS = {
  'EBOS': { lat: 51.1989, lon: 2.8622, name: 'Ostend-Bruges' },
  'EBKT': { lat: 50.8172, lon: 3.2047, name: 'Kortrijk-Wevelgem' },
  'EHRD': { lat: 51.9569, lon: 4.4372, name: 'Rotterdam' },
  'EHBK': { lat: 50.9117, lon: 5.7706, name: 'Maastricht' },
  'EHAM': { lat: 52.3086, lon: 4.7639, name: 'Amsterdam Schiphol' },
  'LFQQ': { lat: 50.5617, lon: 3.0894, name: 'Lille' },
  'EBBR': { lat: 50.9014, lon: 4.4844, name: 'Brussels' },
  'EBAW': { lat: 51.1894, lon: 4.4603, name: 'Antwerp' },
  'LEMG': { lat: 36.6749, lon: -4.4991, name: 'Malaga' },
  'LERS': { lat: 41.1474, lon: 1.1672, name: 'Reus' },
  'LFBE': { lat: 44.8253, lon: 0.5186, name: 'Bergerac' },
  'LFMP': { lat: 42.7403, lon: 2.8706, name: 'Perpignan' },
  'LICD': { lat: 35.4979, lon: 12.6181, name: 'Lampedusa' },
  'LIEE': { lat: 39.2515, lon: 9.0543, name: 'Cagliari' },
  'LMML': { lat: 35.8575, lon: 14.4775, name: 'Malta' }
};

// Utility functions
const formatTime = (date) => {
  return date.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC'
  });
};

const formatDate = (date) => {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

// Header Component
const SignageHeader = ({ lastRefresh, onRefresh, isRefreshing, onLogoClick }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <header className="signage-header" data-testid="signage-header">
      <div className="signage-logo">
        <img 
          src={NSAS_LOGO} 
          alt="NSAS - North Sea Aviation Services" 
          className="nsas-logo"
          style={{ height: '40px', width: 'auto', cursor: 'pointer' }}
          onClick={onLogoClick}
          title="Back to Home"
        />
      </div>
      <div className="signage-clock" data-testid="signage-clock" style={{ textAlign: 'center' }}>
        <div>{formatDate(currentTime)}</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{formatTime(currentTime)} UTC</div>
      </div>
      <div className="signage-status">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <span style={{ 
            fontFamily: 'Barlow Condensed, sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#ffffff'
          }}>OPS DASHBOARD</span>
          <div className="refresh-indicator" style={{ justifyContent: 'flex-end' }}>
            <span className={`refresh-dot ${isRefreshing ? 'refreshing' : ''}`}></span>
            <span>{lastRefresh ? formatTime(lastRefresh) : '--:--:--'}</span>
          </div>
        </div>
        <button 
          onClick={onRefresh}
          disabled={isRefreshing}
          style={{ 
            background: 'transparent', 
            border: 'none', 
            color: 'white', 
            cursor: isRefreshing ? 'wait' : 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
          data-testid="refresh-btn"
        >
          <RefreshCw size={20} className={isRefreshing ? 'spin' : ''} />
        </button>
      </div>
    </header>
  );
};

// Flight Table Component
const FlightPanel = ({ flights, loading, selectedDate, onDateChange, allFlights }) => {
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [liveStreamFlight, setLiveStreamFlight] = useState(null);
  const [streamMode, setStreamMode] = useState('webrtc'); // 'webrtc' or 'hls'
  const [streamStatus, setStreamStatus] = useState('loading'); // 'loading', 'connected', 'error'
  const streamTimeoutRef = useRef(null);
  
  // Use shared media server connection hook (don't auto-check, check on demand)
  const { isConnected: mediaServerConnected, checkConnection } = useMediaServerConnection(false);
  
  // Modal position and size state
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalSize, setModalSize] = useState({ width: 854, height: 480 }); // 16:9 default
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false); // Prevents close right after interaction
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  
  // Center modal on open
  const centerModal = () => {
    const width = 854;
    const height = 480;
    setModalSize({ width, height });
    setModalPosition({
      x: (window.innerWidth - width) / 2,
      y: (window.innerHeight - height) / 2
    });
  };
  
  // Handle drag start
  const handleDragStart = (e) => {
    if (e.target.closest('.live-stream-close') || 
        e.target.closest('.mode-btn') || 
        e.target.closest('.stream-retry-btn') ||
        e.target.closest('.resize-handle')) return;
    
    setIsDragging(true);
    setIsInteracting(true);
    setDragOffset({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    });
  };
  
  // Handle drag
  const handleDrag = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = Math.max(0, Math.min(window.innerWidth - modalSize.width, e.clientX - dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - modalSize.height, e.clientY - dragOffset.y));
    
    setModalPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, modalSize]);
  
  // Handle drag end
  const handleDragEnd = useCallback((e) => {
    e.stopPropagation();
    setIsDragging(false);
    setIsResizing(false);
    // Small delay before allowing overlay click to close
    setTimeout(() => setIsInteracting(false), 100);
  }, []);
  
  // Handle resize start
  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setIsInteracting(true);
  };
  
  // Handle resize (maintain 16:9 aspect ratio)
  const handleResize = useCallback((e) => {
    if (!isResizing) return;
    
    const minWidth = 480;
    const maxWidth = window.innerWidth - modalPosition.x - 20;
    
    const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX - modalPosition.x));
    const newHeight = newWidth * (9 / 16); // Maintain 16:9 aspect ratio
    
    // Check if height fits in viewport
    if (modalPosition.y + newHeight > window.innerHeight - 20) {
      const adjustedHeight = window.innerHeight - modalPosition.y - 20;
      const adjustedWidth = adjustedHeight * (16 / 9);
      setModalSize({ width: adjustedWidth, height: adjustedHeight });
    } else {
      setModalSize({ width: newWidth, height: newHeight });
    }
  }, [isResizing, modalPosition]);
  
  // Add/remove event listeners for drag and resize
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', isDragging ? handleDrag : handleResize);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', isDragging ? handleDrag : handleResize);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, isResizing, handleDrag, handleResize, handleDragEnd]);
  
  // Get stream URL based on mode
  const getStreamUrl = (flight) => {
    // First check if flight has a direct video stream URL (for external aircraft)
    if (flight.video_stream_url) {
      return flight.video_stream_url;
    }
    
    const designator = getFTXDesignator(flight);
    const baseUrl = LIVE_STREAM_URLS[designator];
    if (!baseUrl) return null;
    
    // New NSAS player uses simple URL format: ?id=streamId
    // HLS mode is not supported on the new player - WebRTC only
    return baseUrl;
  };
  
  // Open live stream modal
  const openLiveStream = async (flight) => {
    setStreamStatus('loading');
    setLiveStreamFlight(flight);
    centerModal();
    
    // Check media server connection first
    const connectionResult = await checkConnection();
    
    if (!connectionResult.connected) {
      setStreamStatus('error');
      return;
    }
    
    // Set timeout as fallback - if not connected within 15 seconds, show error
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
    }
    streamTimeoutRef.current = setTimeout(() => {
      setStreamStatus(prev => prev === 'loading' ? 'error' : prev);
    }, 15000);
  };
  
  // Handle stream mode change
  const changeStreamMode = async (mode) => {
    setStreamMode(mode);
    setStreamStatus('loading');
    
    // Check media server connection first
    const connectionResult = await checkConnection();
    
    if (!connectionResult.connected) {
      setStreamStatus('error');
      return;
    }
    
    // Reset timeout for new mode
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
    }
    streamTimeoutRef.current = setTimeout(() => {
      setStreamStatus(prev => prev === 'loading' ? 'error' : prev);
    }, 15000);
  };
  
  // Close modal and cleanup
  const closeLiveStream = () => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
    }
    setLiveStreamFlight(null);
    setStreamStatus('loading');
  };
  
  // Format date for display (e.g., "23 Jan")
  const formatFlightDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00Z');
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  // Format selected date for header
  const formatHeaderDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);
    
    const diffDays = Math.round((selected - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today's Flights";
    if (diffDays === 1) return "Tomorrow's Flights";
    if (diffDays === -1) return "Yesterday's Flights";
    
    return `Flights - ${selected.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
  };

  // Get status badge class with colors
  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'READY':
        return 'badge-ready';
      case 'ACTIVE':
        return 'badge-active';
      case 'PROCESSED':
      case 'EXECUTED':
        return 'badge-executed';
      case 'ON TIME':
        return 'badge-on-time';
      case 'DELAYED':
        return 'badge-delayed';
      case 'CANCELLED':
      case 'CANCELED':
        return 'badge-cancelled';
      case 'COMPLETED':
        return 'badge-completed';
      default:
        return 'badge-scheduled';
    }
  };
  
  // Map status for display (PROCESSED -> EXECUTED)
  const getDisplayStatus = (status) => {
    if (status?.toUpperCase() === 'PROCESSED') {
      return 'EXECUTED';
    }
    return status;
  };

  // Handle date navigation
  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    onDateChange(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    onDateChange(newDate);
  };

  // Handle flight row click
  const handleFlightClick = (flight) => {
    setSelectedFlight(flight);
  };

  // Close popup
  const closePopup = () => {
    setSelectedFlight(null);
  };
  
  if (loading) {
    return (
      <div className="signage-panel" data-testid="flight-panel">
        <div className="signage-panel-header">
          <span>Upcoming Flights</span>
        </div>
        <div className="ops-loading-container">
          <div className="nsas-loader-3d">
            <img src={NSAS_LOGO} alt="Loading" className="nsas-logo-spin" />
          </div>
          <span className="loading-text">Loading flights...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="signage-panel" data-testid="flight-panel">
      <div className="signage-panel-header">
        <span className="header-title">
          {formatHeaderDate(selectedDate)}
        </span>
        <div className="header-nav">
          <span className="header-date">{selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
          <button className="nav-btn" onClick={handlePrevDay} title="Previous day">
            <ChevronLeft size={20} />
          </button>
          <button className="nav-btn" onClick={handleNextDay} title="Next day">
            <ChevronRight size={20} />
          </button>
          <span className="panel-count">{flights.length}</span>
        </div>
      </div>
      <div className="signage-panel-content">
        {flights.length === 0 ? (
          <div className="empty-state">
            <Plane className="empty-state-icon" size={48} />
            <span>No flights for this date</span>
          </div>
        ) : (
          <table className="flight-table tv-optimized">
            <thead>
              <tr>
                <th>Flight</th>
                <th>Time</th>
                <th>Route</th>
                <th>Aircraft</th>
                <th className="hide-mobile">Crew</th>
                <th className="hide-mobile">Status</th>
              </tr>
            </thead>
            <tbody>
              {flights.map((flight, index) => (
                <tr 
                  key={flight.id || index} 
                  className={`animate-fade-in flight-row-clickable ${flight.status?.toUpperCase() === 'ACTIVE' ? 'active-flight-row' : ''}`}
                  style={{ animationDelay: `${index * 50}ms`, position: 'relative' }}
                  onClick={() => handleFlightClick(flight)}
                >
                  <td style={{ position: 'relative' }}>
                    {/* Dollar sign animation for active flights */}
                    {flight.status?.toUpperCase() === 'ACTIVE' && (
                      <div className="dollar-rain-container" style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: '-500%', 
                        bottom: 0,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        zIndex: 0
                      }}>
                        {[...Array(20)].map((_, i) => (
                          <span key={i} className="dollar-sign" style={{ 
                            left: `${2 + i * 5}%`,
                            animationDelay: `${i * 0.25}s`,
                            animationDuration: `${2 + Math.random() * 2}s`
                          }}>$</span>
                        ))}
                      </div>
                    )}
                    <span className="flight-number-large">{flight.flight_number || flight.callsign || '-'}</span>
                    {/* Live stream camera icon for active flights */}
                    {(flight.status?.toUpperCase() === 'ACTIVE' || flight.status?.toUpperCase() === 'EXECUTED' || flight.status?.toUpperCase() === 'PROCESSED') && 
                     (LIVE_STREAM_URLS[getFTXDesignator(flight)] || flight.video_stream_url) && (
                      <button
                        className="live-stream-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openLiveStream(flight);
                        }}
                        title="View Live Stream"
                        data-testid="live-stream-btn"
                      >
                        <Video size={16} />
                        <span className="live-indicator">LIVE</span>
                      </button>
                    )}
                  </td>
                  <td>
                    {flight.is_external ? (
                      <div className="time-display-large" style={{opacity: 0.5}}>—</div>
                    ) : (
                      <>
                        <div className="time-display-large">{flight.departure_time || '-'}Z</div>
                        <div className="time-arrival">→ {flight.arrival_time || '-'}Z</div>
                      </>
                    )}
                  </td>
                  <td>
                    <div className="route-display">
                      <span className="airport-code-large">{flight.departure_airport || '-'}</span>
                      <span className="route-arrow">→</span>
                      <span className="airport-code-large">{flight.arrival_airport || '-'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="aircraft-reg-large">{flight.aircraft_reg || '-'}</div>
                    <div className="aircraft-type">{flight.aircraft_type || ''}</div>
                  </td>
                  <td className="hide-mobile">
                    <div className="crew-list-large">
                      {(flight.crew || []).map((member, i) => (
                        <span key={i} className="crew-member">{member}</span>
                      ))}
                    </div>
                  </td>
                  <td className="hide-mobile">
                    <span className={`badge-large ${getStatusBadgeClass(flight.status)}`}>
                      {getDisplayStatus(flight.status) || 'SCHEDULED'}
                      {(flight.status?.toUpperCase() === 'PROCESSED' || flight.status?.toUpperCase() === 'EXECUTED') && (
                        <span className="executed-star">★</span>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Flight Details Popup */}
      {selectedFlight && (
        <div className="flight-popup-overlay" onClick={closePopup}>
          <div className="flight-popup" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={closePopup}>
              <X size={24} />
            </button>
            <h3 className="popup-title">{selectedFlight.flight_number || 'Flight Details'}</h3>
            <div className="popup-content">
              <div className="popup-row">
                <span className="popup-label">Route:</span>
                <span className="popup-value">{selectedFlight.departure_airport} → {selectedFlight.arrival_airport}</span>
              </div>
              <div className="popup-row">
                <span className="popup-label">Scheduled Time:</span>
                <span className="popup-value">{selectedFlight.departure_time}Z - {selectedFlight.arrival_time}Z</span>
              </div>
              {(selectedFlight.status?.toUpperCase() === 'EXECUTED' || selectedFlight.status?.toUpperCase() === 'PROCESSED') && selectedFlight.actual_departure_time && (
                <>
                  <div className="popup-row popup-actual-times">
                    <span className="popup-label">Actual Times:</span>
                    <span className="popup-value actual-highlight">
                      {selectedFlight.actual_departure_time}Z - {selectedFlight.actual_arrival_time}Z
                      {selectedFlight.block_time && <span className="block-time-badge">Block: {selectedFlight.block_time}</span>}
                    </span>
                  </div>
                  {(selectedFlight.actual_takeoff_time || selectedFlight.actual_landing_time) && (
                    <div className="popup-row popup-flight-times">
                      <span className="popup-label">Takeoff/Landing:</span>
                      <span className="popup-value">
                        {selectedFlight.actual_takeoff_time || '-'}Z / {selectedFlight.actual_landing_time || '-'}Z
                      </span>
                    </div>
                  )}
                </>
              )}
              <div className="popup-row">
                <span className="popup-label">Aircraft:</span>
                <span className="popup-value">{selectedFlight.aircraft_reg} ({selectedFlight.aircraft_type})</span>
              </div>
              <div className="popup-row">
                <span className="popup-label">Crew:</span>
                <span className="popup-value">{(selectedFlight.crew || []).join(', ') || '-'}</span>
              </div>
              <div className="popup-row">
                <span className="popup-label">Status:</span>
                <span className={`badge-large ${getStatusBadgeClass(selectedFlight.status)}`}>
                  {getDisplayStatus(selectedFlight.status) || 'SCHEDULED'}
                  {(selectedFlight.status?.toUpperCase() === 'PROCESSED' || selectedFlight.status?.toUpperCase() === 'EXECUTED') && (
                    <span className="executed-star">★</span>
                  )}
                </span>
              </div>
              <div className="popup-remarks">
                <span className="popup-label">Remarks:</span>
                <div className="remarks-content">
                  {selectedFlight.remarks || selectedFlight.notes || 'No remarks for this flight'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Live Stream Popup */}
      {liveStreamFlight && (
        <div className="live-stream-overlay" onClick={(e) => {
          if (!isDragging && !isResizing && !isInteracting) closeLiveStream();
        }}>
          <div 
            ref={modalRef}
            className={`live-stream-modal ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
            style={{
              position: 'fixed',
              left: `${modalPosition.x}px`,
              top: `${modalPosition.y}px`,
              width: `${modalSize.width}px`,
              height: `${modalSize.height + 100}px`, // +100 for header and footer
              transform: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="live-stream-header"
              onMouseDown={handleDragStart}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <div className="live-stream-title">
                <Video size={20} />
                <span>Live Stream - {getFTXDesignator(liveStreamFlight)}</span>
                <span className="live-badge">LIVE</span>
              </div>
              <div className="stream-mode-toggle">
                <button 
                  className={`mode-btn ${streamMode === 'webrtc' ? 'active' : ''}`}
                  onClick={() => changeStreamMode('webrtc')}
                  title="Ultra-low latency"
                >
                  WebRTC
                </button>
                <button 
                  className={`mode-btn ${streamMode === 'hls' ? 'active' : ''}`}
                  onClick={() => changeStreamMode('hls')}
                  title="More stable, higher latency"
                >
                  HLS
                </button>
              </div>
              <button 
                className="live-stream-close"
                onClick={closeLiveStream}
                data-testid="close-live-stream"
              >
                <X size={24} />
              </button>
            </div>
            <div className="live-stream-content" style={{ height: `${modalSize.height}px` }}>
              {streamStatus === 'error' ? (
                <div className="stream-error-container stream-security-error">
                  <div className="security-icon-container">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="120" 
                      height="120" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#dc2626" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="stream-security-icon"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </div>
                  <h3 className="stream-security-title">SECURED CONNECTION REQUIRED</h3>
                  <div className="stream-security-divider"></div>
                  <p className="stream-security-message">ACCESS DENIED</p>
                  <p className="stream-security-hint">
                    You must be connected to the <strong>NSAS VPN</strong><br/>
                    or be physically present at <strong>NSAS Headquarters</strong><br/>
                    to access this live stream.
                  </p>
                  <button 
                    className="stream-retry-btn"
                    onClick={async () => {
                      setStreamStatus('loading');
                      if (streamTimeoutRef.current) clearTimeout(streamTimeoutRef.current);
                      
                      // Check connection first
                      const connectionResult = await checkConnection(true);
                      if (!connectionResult.connected) {
                        setStreamStatus('error');
                        return;
                      }
                      
                      streamTimeoutRef.current = setTimeout(() => {
                        setStreamStatus(prev => prev === 'loading' ? 'error' : prev);
                      }, 15000);
                    }}
                  >
                    Retry Connection
                  </button>
                </div>
              ) : (
                <>
                  {streamStatus === 'loading' && (
                    <div className="stream-loading-overlay">
                      <div className="nsas-loader-3d">
                        <img src={NSAS_LOGO} alt="Loading" className="nsas-logo-spin" />
                      </div>
                      <p>Connecting to stream...</p>
                    </div>
                  )}
                  <iframe
                    key={`${streamMode}-${streamStatus}`}
                    src={getStreamUrl(liveStreamFlight)}
                    title={`Live Stream - ${getFTXDesignator(liveStreamFlight)}`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                    allowFullScreen
                    onLoad={() => setStreamStatus('connected')}
                  />
                </>
              )}
            </div>
            <div className="live-stream-footer">
              <span className="stream-info">
                Flight: {liveStreamFlight.flight_number || liveStreamFlight.callsign} | 
                Aircraft: {liveStreamFlight.aircraft_reg} | 
                Route: {liveStreamFlight.departure_airport} → {liveStreamFlight.arrival_airport}
              </span>
              {streamStatus === 'connected' && (
                <span className="stream-mode-info">
                  {streamMode === 'webrtc' ? '⚡ Ultra-low latency' : '📺 Stable playback'}
                </span>
              )}
            </div>
            {/* Resize handle */}
            <div 
              className="resize-handle"
              onMouseDown={handleResizeStart}
              title="Drag to resize"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14ZM14 6H12V4H14V6ZM10 10H8V8H10V10ZM6 14H4V12H6V14Z"/>
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Weather Panel Component
const WeatherPanel = ({ flights }) => {
  const [selectedFlightIndex, setSelectedFlightIndex] = useState(0);
  
  // Reset to first flight when flights list changes
  useEffect(() => {
    setSelectedFlightIndex(0);
  }, [flights.length]);
  
  const selectedFlight = flights[selectedFlightIndex] || flights[0];
  const airport = selectedFlight?.departure_airport || 'EBOS';
  const flightNumber = selectedFlight?.flight_number || '';
  const flightTime = selectedFlight?.departure_time ? `${selectedFlight.departure_time}Z` : '';
  const flightDate = selectedFlight?.flight_date || '';
  const isTomorrow = selectedFlight?.is_tomorrow;
  const coords = AIRPORT_COORDS[airport] || AIRPORT_COORDS['EBOS'];
  const flightCategory = selectedFlight?.flight_category || '';
  
  // Format date for display
  const formatFlightDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00Z');
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };
  
  const weatherData = selectedFlight ? {
    metar: selectedFlight.metar,
    taf: selectedFlight.taf
  } : null;

  // Flight category color
  const getCategoryColor = (cat) => {
    switch(cat) {
      case 'VFR': return '#16a34a';
      case 'MVFR': return '#2563eb';
      case 'IFR': return '#dc2626';
      case 'LIFR': return '#7c3aed';
      default: return '#64748b';
    }
  };

  // Calculate Windy timestamp for flight time
  const getWindyTimestamp = () => {
    if (selectedFlight?.flight_date && selectedFlight?.departure_time) {
      try {
        const [hours, minutes] = selectedFlight.departure_time.split(':');
        const flightDateTime = new Date(`${selectedFlight.flight_date}T${hours}:${minutes}:00Z`);
        return Math.floor(flightDateTime.getTime() / 1000);
      } catch (e) {
        return '';
      }
    }
    return '';
  };
  
  const windyTimestamp = getWindyTimestamp();
  const windyUrl = `https://embed.windy.com/embed2.html?lat=${coords.lat}&lon=${coords.lon}&zoom=7&level=surface&overlay=radar&product=radar&menu=&message=true&marker=true&calendar=${windyTimestamp || 'now'}&pressure=&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;
  const windyUrlLarge = `https://embed.windy.com/embed2.html?lat=${coords.lat}&lon=${coords.lon}&zoom=8&level=surface&overlay=radar&product=radar&menu=&message=true&marker=true&calendar=${windyTimestamp || 'now'}&pressure=&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;

  const [showWindyPopup, setShowWindyPopup] = useState(false);

  return (
    <div className="signage-panel" data-testid="weather-panel">
      <div className="signage-panel-header">
        <span>
          WX Info 
          {flightNumber && <span style={{ color: '#3b82f6', marginLeft: '8px' }}>{flightNumber}</span>} 
          {flightDate && <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginLeft: '6px' }}>({formatFlightDate(flightDate)})</span>}
          {flightTime && <span style={{ color: '#64748b', fontSize: '0.9rem', marginLeft: '4px' }}>@ {flightTime}</span>}
          {isTomorrow && <span style={{ color: '#f59e0b', fontSize: '0.7rem', marginLeft: '8px', fontWeight: '500' }}>TOMORROW</span>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {flightCategory && (
            <span style={{ 
              backgroundColor: getCategoryColor(flightCategory), 
              color: 'white', 
              padding: '2px 8px', 
              borderRadius: '2px',
              fontSize: '0.75rem',
              fontWeight: 600
            }}>
              {flightCategory}
            </span>
          )}
          <span className="panel-count">{airport}</span>
        </div>
      </div>
      <div className="signage-panel-content" style={{ padding: '0', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Flight selector tabs */}
        {flights.length > 1 && (
          <div style={{ 
            display: 'flex', 
            gap: '4px', 
            padding: '8px 12px', 
            borderBottom: '1px solid #334155',
            backgroundColor: '#1e293b',
            overflowX: 'auto'
          }}>
            {flights.map((flight, index) => (
              <button
                key={flight.id || index}
                onClick={() => setSelectedFlightIndex(index)}
                data-testid={`flight-tab-${index}`}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  backgroundColor: selectedFlightIndex === index ? '#3b82f6' : '#334155',
                  color: selectedFlightIndex === index ? 'white' : '#94a3b8'
                }}
              >
                {flight.flight_number || `Flight ${index + 1}`}
                <span style={{ 
                  marginLeft: '6px', 
                  fontSize: '0.75rem',
                  opacity: 0.8 
                }}>
                  {flight.departure_airport}
                </span>
              </button>
            ))}
          </div>
        )}
        
        <div 
          className="windy-iframe-container" 
          style={{ flex: 1, minHeight: '180px', borderBottom: '2px solid #E4E4E7', position: 'relative', cursor: 'pointer' }}
          onClick={() => setShowWindyPopup(true)}
          title="Click to enlarge weather map"
        >
          <iframe
            src={windyUrl}
            style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }}
            title="Weather Map"
          />
          {/* Expand icon overlay */}
          <div style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            background: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
            Expand
          </div>
        </div>
        
        {/* Windy Map Popup Modal */}
        {showWindyPopup && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setShowWindyPopup(false)}
          >
            <div 
              style={{
                width: '95vw',
                height: '90vh',
                maxWidth: '1600px',
                background: '#1a1a2e',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%)',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #334155'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold', 
                    color: '#fbbf24',
                    fontFamily: 'Barlow Condensed, sans-serif',
                    letterSpacing: '0.05em'
                  }}>
                    WEATHER MAP
                  </span>
                  <span style={{ 
                    fontSize: '1rem', 
                    color: '#94a3b8',
                    fontFamily: 'Barlow Condensed, sans-serif'
                  }}>
                    {airport} - {coords.name}
                  </span>
                </div>
                <button
                  onClick={() => setShowWindyPopup(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                >
                  <X size={18} />
                  Close
                </button>
              </div>
              
              {/* Large Windy Map */}
              <iframe
                src={windyUrlLarge}
                style={{ width: '100%', height: 'calc(100% - 56px)', border: 'none' }}
                title="Weather Map - Expanded"
                allowFullScreen
              />
            </div>
          </div>
        )}
        
        <div style={{ padding: '12px', overflowY: 'auto', maxHeight: '200px' }}>
          <div style={{ 
            fontFamily: 'Barlow Condensed, sans-serif',
            fontWeight: 700,
            fontSize: '1.1rem',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span className="airport-code">{airport}</span>
            <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#71717A' }}>
              {coords.name}
            </span>
          </div>
          
          {weatherData?.metar ? (
            <>
              <div className="weather-box">
                <div className="weather-label">METAR</div>
                <div className="weather-text">{weatherData.metar}</div>
              </div>
              {weatherData.taf && (
                <div className="weather-box" style={{ marginTop: '6px' }}>
                  <div className="weather-label">TAF</div>
                  <div className="weather-text">{weatherData.taf}</div>
                </div>
              )}
            </>
          ) : (
            <div className="weather-box">
              <div className="weather-text" style={{ color: '#71717A' }}>Weather data loads with flights</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Message Board Panel Component (renamed from Fleet Info/Defects)
const FleetInfoPanel = ({ defects, loading, onDelete }) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  // Data is already sorted by date (newest first) from the backend
  const sortedDefects = defects;

  const handlePartyHover = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 11000);
  };

  const handleMessageClick = (defect, e) => {
    // Don't open modal if clicking on delete button or party emoji
    if (e.target.closest('button') || e.target.closest('.party-trigger')) return;
    setSelectedMessage(defect);
  };

  const closeModal = () => {
    setSelectedMessage(null);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // If already formatted (contains comma), return as is
    if (dateStr.includes(',') && dateStr.includes('/')) {
      return dateStr;
    }
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if text contains party emoji
  const renderReportedBy = (reportedBy) => {
    if (reportedBy && reportedBy.includes('🥳')) {
      return (
        <span 
          className="party-trigger"
          onMouseEnter={handlePartyHover}
        >
          {reportedBy}
        </span>
      );
    }
    return reportedBy;
  };

  if (loading) {
    return (
      <div className="signage-panel fleet-info-panel" data-testid="defects-panel">
        <div className="signage-panel-header">
          <span>Message Board</span>
        </div>
        <div className="ops-loading-container">
          <div className="nsas-loader-3d">
            <img src={NSAS_LOGO} alt="Loading" className="nsas-logo-spin" />
          </div>
          <span className="loading-text">Loading messages...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="signage-panel fleet-info-panel" data-testid="defects-panel">
      <Confetti active={showConfetti} />
      <div className="signage-panel-header">
        <span>Message Board</span>
        <span className="panel-count">{defects.length}</span>
      </div>
      <div className="signage-panel-content" style={{ overflowY: 'auto', maxHeight: 'calc(100% - 50px)' }}>
        {sortedDefects.length === 0 ? (
          <div className="empty-state">
            <CheckCircle className="empty-state-icon" size={48} />
            <span>No active messages</span>
          </div>
        ) : (
          sortedDefects.map((defect, index) => (
            <div 
              key={defect.id}
              className={`defect-card animate-fade-in ${defect.is_highlighted ? 'highlighted highlight-pulse' : ''} ${defect.priority === 'AOG' ? 'aog' : ''}`}
              style={{ animationDelay: `${index * 50}ms`, display: 'flex', gap: '16px', alignItems: 'flex-start', cursor: 'pointer' }}
              data-testid={`defect-card-${defect.id}`}
              onClick={(e) => handleMessageClick(defect, e)}
            >
              {/* Circle on the left - Trigram for local, SkyLegs icon for skylegs */}
              <div style={{
                width: '60px',
                height: '60px',
                minWidth: '60px',
                maxWidth: '60px',
                minHeight: '60px',
                maxHeight: '60px',
                borderRadius: '50%',
                backgroundColor: defect.source === 'skylegs' ? '#1e3a5f' : '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                aspectRatio: '1 / 1'
              }}>
                {defect.source === 'skylegs' ? (
                  <img 
                    src="https://nsas.skylegs.com/img/favicons/favicon-default.ico" 
                    alt="SkyLegs" 
                    style={{ width: '36px', height: '36px', objectFit: 'contain' }} 
                  />
                ) : (
                  <span style={{
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    color: '#ffffff',
                    textTransform: 'uppercase'
                  }}>
                    {(defect.reported_by || '?').substring(0, 3)}
                  </span>
                )}
              </div>
              
              {/* Main content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Top row: Aircraft + badges on left, crew attention + date on right */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '8px',
                  gap: '16px'
                }}>
                  {/* Left side: Aircraft/Announcement and badges */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {defect.aircraft_reg === 'GENERAL' ? (
                      <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#60a5fa', minWidth: '90px' }}>
                        {defect.reported_by && defect.reported_by.toUpperCase().startsWith('XDU') && (
                          <span 
                            className="party-trigger"
                            onMouseEnter={handlePartyHover}
                            style={{ cursor: 'pointer', marginRight: '4px' }}
                            title="Hover for celebration!"
                          >
                            🥳
                          </span>
                        )}
                        📢 General Announcement
                      </span>
                    ) : (
                      <span className="aircraft-reg" style={{ fontSize: '1.5rem', fontWeight: 700, minWidth: '90px' }}>{defect.aircraft_reg}</span>
                    )}
                    <span className={`badge badge-${defect.priority.toLowerCase()}`} style={{ fontSize: '0.9rem', padding: '5px 12px' }}>
                      {defect.priority === 'AOG' && <span className="aog-blink" style={{ marginRight: '4px' }}>●</span>}
                      {defect.priority}
                    </span>
                    {defect.kind && (
                      <span className="badge" style={{ backgroundColor: '#475569', color: '#e2e8f0', fontSize: '0.8rem', padding: '4px 10px' }}>
                        {defect.kind.toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  {/* Right side: Crew attention first, then date, then close button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                    {defect.is_highlighted && (
                      <span className="crew-attention-label" style={{ fontSize: '0.9rem', flexShrink: 0 }}>
                        ★ CREW ATTENTION
                      </span>
                    )}
                    <div style={{ fontSize: '0.9rem', color: '#a1a1aa', textAlign: 'right' }}>
                      {formatDate(defect.reported_at)}
                    </div>
                    {/* Close button for local messages */}
                    {defect.source === 'local' && onDelete && (
                      <button
                        onClick={() => onDelete(defect.id)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ef4444',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#ef4444',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#ef4444';
                          e.currentTarget.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#ef4444';
                        }}
                        data-testid={`delete-defect-${defect.id}`}
                        title="Delete message"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Main description - more compact font for message board */}
                <div style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 500, 
                  color: '#f4f4f5',
                  lineHeight: '1.35',
                  wordBreak: 'break-word'
                }}>
                  {defect.description}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Message Detail Modal */}
      {selectedMessage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={closeModal}
        >
          <div 
            style={{
              backgroundColor: '#1a1a2e',
              borderRadius: '8px',
              border: '1px solid #3b82f6',
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ 
                color: '#f4f4f5', 
                fontSize: '1.5rem', 
                fontWeight: 700, 
                margin: 0,
                fontFamily: 'Barlow Condensed, sans-serif',
                textTransform: 'uppercase'
              }}>
                Message Details
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#a1a1aa',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Modal Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Aircraft / Type */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Aircraft:</span>
                <span style={{ color: '#60a5fa', fontWeight: 600, fontSize: '1.2rem' }}>
                  {selectedMessage.aircraft_reg === 'GENERAL' ? '📢 General Announcement' : selectedMessage.aircraft_reg}
                </span>
              </div>
              
              {/* Priority */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Priority:</span>
                <span className={`badge badge-${selectedMessage.priority.toLowerCase()}`} style={{ fontSize: '0.9rem', padding: '5px 12px' }}>
                  {selectedMessage.priority}
                </span>
              </div>
              
              {/* Kind/Type (for SkyLegs) */}
              {selectedMessage.kind && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Type:</span>
                  <span style={{ color: '#e2e8f0', textTransform: 'uppercase' }}>{selectedMessage.kind}</span>
                </div>
              )}
              
              {/* Description */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Description:</span>
                <span style={{ color: '#f4f4f5', lineHeight: '1.4' }}>{selectedMessage.description}</span>
              </div>
              
              {/* Flight Restrictions */}
              {selectedMessage.flight_restrictions && selectedMessage.flight_restrictions.toLowerCase() !== 'none' && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Restrictions:</span>
                  <span style={{ color: '#f97316', fontWeight: 600 }}>{selectedMessage.flight_restrictions}</span>
                </div>
              )}
              
              {/* Corrective Action */}
              {selectedMessage.corrective_action && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Corrective Action:</span>
                  <span style={{ color: '#34d399', lineHeight: '1.4' }}>{selectedMessage.corrective_action}</span>
                </div>
              )}
              
              {/* Reported By */}
              {selectedMessage.reported_by && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Reported By:</span>
                  <span style={{ color: '#e2e8f0' }}>{selectedMessage.reported_by}</span>
                </div>
              )}
              
              {/* Date */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Date:</span>
                <span style={{ color: '#e2e8f0' }}>{formatDate(selectedMessage.reported_at)}</span>
              </div>
              
              {/* Source */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Source:</span>
                <span style={{ color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {selectedMessage.source === 'skylegs' ? (
                    <>
                      <img src="https://nsas.skylegs.com/img/favicons/favicon-default.ico" alt="SkyLegs" style={{ width: '20px', height: '20px' }} />
                      SkyLegs
                    </>
                  ) : (
                    'Local Entry'
                  )}
                </span>
              </div>
              
              {/* Expires At (for SkyLegs) */}
              {selectedMessage.expires_at && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ color: '#a1a1aa', minWidth: '120px' }}>Expires:</span>
                  <span style={{ color: '#e2e8f0' }}>{formatDate(selectedMessage.expires_at)}</span>
                </div>
              )}
              
              {/* ID */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ color: '#a1a1aa', minWidth: '120px' }}>ID:</span>
                <span style={{ color: '#71717a', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>{selectedMessage.id}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Defect Form Component
const DefectForm = ({ onSubmit, aircraft }) => {
  const [formData, setFormData] = useState({
    aircraft_reg: '',
    description: '',
    priority: 'MEDIUM',
    reported_by: '',
    is_highlighted: true
  });
  const [isGeneralMessage, setIsGeneralMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleGeneralMessageChange = (checked) => {
    setIsGeneralMessage(checked);
    if (checked) {
      setFormData({ ...formData, aircraft_reg: 'GENERAL' });
    } else {
      setFormData({ ...formData, aircraft_reg: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.aircraft_reg || !formData.description || !formData.reported_by) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit(formData);
      setFormData({
        aircraft_reg: isGeneralMessage ? 'GENERAL' : '',
        description: '',
        priority: 'MEDIUM',
        reported_by: '',
        is_highlighted: true
      });
      toast.success('Message added successfully');
    } catch (error) {
      toast.error('Failed to add message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="signage-panel" data-testid="defect-form-panel">
      <div className="signage-panel-header">
        <span>Quick Entry</span>
        <PlusCircle size={18} />
      </div>
      <form className="defect-form" onSubmit={handleSubmit}>
        {/* General Message Checkbox */}
        <div className="checkbox-container" style={{ marginBottom: '12px' }}>
          <input
            type="checkbox"
            id="generalMessage"
            className="checkbox-input"
            checked={isGeneralMessage}
            onChange={(e) => handleGeneralMessageChange(e.target.checked)}
            data-testid="general-message-checkbox"
          />
          <label htmlFor="generalMessage" className="checkbox-label">
            General Message (no aircraft)
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div className="form-group">
            <label className="form-label" style={{ opacity: isGeneralMessage ? 0.5 : 1 }}>Aircraft *</label>
            <select
              className="form-select"
              value={isGeneralMessage ? 'GENERAL' : formData.aircraft_reg}
              onChange={(e) => setFormData({ ...formData, aircraft_reg: e.target.value })}
              disabled={isGeneralMessage}
              style={{ opacity: isGeneralMessage ? 0.5 : 1, cursor: isGeneralMessage ? 'not-allowed' : 'pointer' }}
              data-testid="aircraft-select"
            >
              {isGeneralMessage ? (
                <option value="GENERAL">General Announcement</option>
              ) : (
                <>
                  <option value="">Select Aircraft</option>
                  {aircraft.map(reg => (
                    <option key={reg} value={reg}>{reg}</option>
                  ))}
                </>
              )}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Priority *</label>
            <select
              className="form-select"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              data-testid="priority-select"
            >
              <option value="AOG">AOG - Aircraft on Ground</option>
              <option value="HIGH">HIGH</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="LOW">LOW</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Message *</label>
          <textarea
            className="form-input form-textarea"
            placeholder="Enter message..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            data-testid="description-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Reported By *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Your name/trigram"
            value={formData.reported_by}
            onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
            data-testid="reported-by-input"
          />
        </div>

        <div className="checkbox-container">
          <input
            type="checkbox"
            id="highlight"
            className="checkbox-input"
            checked={formData.is_highlighted}
            onChange={(e) => setFormData({ ...formData, is_highlighted: e.target.checked })}
            data-testid="highlight-checkbox"
          />
          <label htmlFor="highlight" className="checkbox-label">
            Highlight for next crew attention
          </label>
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={submitting}
          data-testid="submit-defect-btn"
        >
          {submitting ? 'Submitting...' : 'Add Message'}
        </button>
      </form>
    </div>
  );
};

// Main Ops Dashboard Component
export default function OpsDashboard() {
  const navigate = useNavigate();
  const [allFlights, setAllFlights] = useState([]);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Set document title
  useEffect(() => {
    document.title = 'NSAS OPS DASHBOARD';
  }, []);

  // NSAS Aircraft fleet
  const aircraft = ['OO-MSA', 'OO-MSB', 'OO-MSC', 'OO-MSD'];

  // Filter flights by selected date
  const getFlightsForDate = useCallback((date) => {
    const dateStr = date.toISOString().split('T')[0];
    return allFlights.filter(f => f.flight_date === dateStr);
  }, [allFlights]);

  const flights = getFlightsForDate(selectedDate);

  const fetchData = useCallback(async (forceRefresh = false) => {
    setIsRefreshing(true);
    try {
      // Fetch multiple days of flight data (7 days ahead) with metadata
      // Use refresh=true for the 5-minute interval refresh to force API call
      const refreshParam = forceRefresh ? '&refresh=true' : '';
      const [flightsRes, troublesRes, localDefectsRes] = await Promise.all([
        axios.get(`${API}/ops/skylegs/flights?days=7&include_meta=true${refreshParam}`),
        axios.get(`${API}/ops/skylegs/troubles`),
        axios.get(`${API}/ops/defects`)
      ]);
      
      // Handle both formats: with metadata or just array
      const flightsData = flightsRes.data?.flights || flightsRes.data || [];
      const meta = flightsRes.data?.meta;
      
      // Only log if no flights found - don't show toast as this is expected behavior
      if (Array.isArray(flightsData) && flightsData.length === 0) {
        console.log('No flight data available for this period');
      }
      
      // Combine SkyLegs troubles with local defects
      const skylegsTroubles = troublesRes.data || [];
      const localDefects = (localDefectsRes.data || []).map(d => ({
        ...d,
        source: 'local',
        sort_date: d.reported_at || new Date().toISOString()
      }));
      
      // Merge and sort by date (newest first)
      const allMessages = [...localDefects, ...skylegsTroubles].sort((a, b) => {
        const dateA = a.sort_date || a.reported_at || '';
        const dateB = b.sort_date || b.reported_at || '';
        return dateB.localeCompare(dateA);
      });
      
      setAllFlights(Array.isArray(flightsData) ? flightsData : []);
      setDefects(allMessages);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Handle date change - just update selected date, data already loaded
  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
  }, []);

  const handleSubmitDefect = async (defectData) => {
    await axios.post(`${API}/ops/defects`, defectData);
    await fetchData();
  };

  const handleDeleteDefect = async (defectId) => {
    try {
      await axios.delete(`${API}/ops/defects/${defectId}`);
      await fetchData();
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  // Initial fetch and auto-refresh
  useEffect(() => {
    // Initial fetch uses cache if available
    fetchData(false);
    
    // 60-minute interval refresh forces API call to get fresh data from SkyLegs
    const refreshInterval = setInterval(() => fetchData(true), REFRESH_INTERVAL);
    
    // ADS-B flight status check every 2 minutes (lightweight, doesn't hit SkyLegs)
    const adsbStatusCheck = setInterval(async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ops/adsb-flight-status`);
        if (response.ok) {
          const data = await response.json();
          // If any status updates detected, refresh the display
          if (data.status_updates && data.status_updates.length > 0) {
            console.log('ADS-B detected flight status changes:', data.status_updates);
            // Trigger a soft refresh to update UI
            fetchData(false);
          }
        }
      } catch (error) {
        console.error('ADS-B status check failed:', error);
      }
    }, 2 * 60 * 1000); // Every 2 minutes
    
    // Check for day change every minute (for auto-refresh at midnight)
    let lastDate = new Date().toDateString();
    const dayChangeCheck = setInterval(() => {
      const currentDate = new Date().toDateString();
      if (currentDate !== lastDate) {
        // Day has changed - update selected date and refresh
        lastDate = currentDate;
        // Force full reload to ensure clean state with new date
        window.location.reload();
      }
    }, 60000); // Check every minute
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(adsbStatusCheck);
      clearInterval(dayChangeCheck);
    };
  }, [fetchData]);

  return (
    <div className="ops-dashboard-app">
      <div className="noise-overlay"></div>
      <div className="signage-container" data-testid="signage-container">
        <SignageHeader lastRefresh={lastRefresh} onRefresh={() => fetchData(false)} isRefreshing={isRefreshing} onLogoClick={() => navigate('/')} />
        
        <div className="signage-grid-container">
          <FlightPanel 
            flights={flights} 
            loading={loading} 
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            allFlights={allFlights}
          />
          <WeatherPanel flights={flights} />
          <div className="bottom-row-wrapper">
            <FleetInfoPanel 
              defects={defects} 
              loading={loading}
              onDelete={handleDeleteDefect}
            />
            <DefectForm onSubmit={handleSubmitDefect} aircraft={aircraft} />
          </div>
        </div>
      </div>
      
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            fontFamily: 'Inter, sans-serif',
          }
        }}
      />
    </div>
  );
}
